import React, { useState, useEffect, useMemo } from 'react';
import { Box, CircularProgress } from '@mui/material';
import Router from '@/router';
import { useNotifications } from '@/hooks/use-notifications';
import { useTranslations } from '@/i18n/use-translations';
import type { z } from 'zod';

import UserDataForms from './user-data-form';
import OrganizationForm from './organization-form';
import UserQuestionaireForms from '../onboarding-content/user-questionnaire-form';
import OnboardingFinishForm from './onboarding-finish-form';

import { supportedLocales, type SupportedLocales } from '@/i18n/i18n';
import { userOnboardingSchema } from '@/schemas/user/onboarding-schema';
import { useGetUserQuery } from '@/state/queries/users/use-get-user';
import { useUserStore } from '@/state/stores/use-user-store';
import { useUpdateUserMutation } from '@/state/mutations/users/use-update-user';
import type { QuestionAnswerDto } from '@/types/dtos/user/request/question-answer.dto';

type FormData = z.infer<typeof userOnboardingSchema>;
type OnboardingStep = 'userForm' | 'questionnaire' | 'organizationForm' | 'onboardingFinish';

function normalizeUserLanguage(language: string | undefined): SupportedLocales {
  if (language && supportedLocales.includes(language as SupportedLocales)) {
    return language as SupportedLocales;
  }
  return 'en';
}

export type OnboardingContentProps = {
  /**
   * When true, user continues with questionnaire and optional organization creation after profile setup.
   * When false, only basic onboarding (profile + finish confirmation) runs.
   */
  extendedOnboarding: boolean;
};

const OnboardingContent: React.FC<OnboardingContentProps> = ({ extendedOnboarding }) => {
  const { data: user } = useGetUserQuery();
  const setUserStore = useUserStore((s) => s.setUser);
  const notify = useNotifications();
  const t = useTranslations();
  const { mutate: updateUser, isPending: updatingUser } = useUpdateUserMutation();

  const [formData, setFormData] = useState<FormData | null>(null);
  const [currentStep, setCurrentStep] = useState<OnboardingStep>('userForm');

  const [answersMap, setAnswersMap] = useState<Record<string, string[]>>({});
  const [commentsMap, setCommentsMap] = useState<Record<string, Record<string, string>>>({});
  const [activeStep, setActiveStep] = useState(0);
  const [teamName, setTeamName] = useState('');

  const [pendingQuestionAnswers, setPendingQuestionAnswers] = useState<QuestionAnswerDto[] | null>(null);

  // Track onboarding completion locally, initialized from user data
  // Treat undefined as false (needs onboarding) when user exists
  const [isOnboarded, setIsOnboarded] = useState<boolean | null>(() => (user ? (user.isOnboarded ?? false) : null));

  // Update local state when user data changes (but not synchronously in effect)
  useEffect(() => {
    if (user) {
      // Use setTimeout to defer state update and avoid synchronous setState in effect
      const timeoutId = setTimeout(() => {
        // Treat undefined as false (needs onboarding)
        setIsOnboarded(user.isOnboarded ?? false);
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [user]);

  // Sync user store when user changes
  useEffect(() => {
    if (user) {
      setUserStore({
        firstName: user.firstName,
        lastName: user.lastName,
        phoneNumber: user.phoneNumber,
        language: user.language,
      });
    }
  }, [user, setUserStore]);

  const handleBackFromOrg = () => setCurrentStep('questionnaire');

  const handleFinishAll = () => {
    notify.notifySuccess(t('onBoarding.notifications.successNotification'));
    setTimeout(() => {
      setIsOnboarded(true);
    }, 150);
  };

  const finishVariant = pendingQuestionAnswers ? 'contributor' : 'basic';

  const userDataFormDefaults = useMemo((): FormData | undefined => {
    if (formData) {
      return formData;
    }
    if (!user) {
      return undefined;
    }
    return {
      firstName: user.firstName?.trim() ?? '',
      lastName: user.lastName?.trim() ?? '',
      language: normalizeUserLanguage(user.language),
      essentialCookiesAccepted: false,
    };
  }, [formData, user]);

  const patchUserCompleteOnboarding = (questionAnswers?: QuestionAnswerDto[]) => {
    if (!formData) return;

    updateUser(
      {
        firstName: formData.firstName,
        lastName: formData.lastName,
        language: formData.language,
        isOnboarded: true,
        cookiePreferences: {
          functional: true,
          analytical: false,
          marketing: false,
        },
        ...(questionAnswers?.length ? { questionAnswers } : {}),
      },
      {
        onSuccess: () => {
          notify.notifySuccess(t('onBoarding.notifications.successNotification'));
          setIsOnboarded(true);
        },
        onError: () => {
          notify.notifyError(t('onBoarding.notifications.errorNotification'));
        },
      },
    );
  };

  if (isOnboarded === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isOnboarded) {
    if (currentStep === 'userForm') {
      return (
        <UserDataForms
          defaultValues={userDataFormDefaults}
          onComplete={(data) => {
            setFormData(data);
            if (extendedOnboarding) {
              setCurrentStep('questionnaire');
            } else {
              setPendingQuestionAnswers(null);
              setCurrentStep('onboardingFinish');
            }
          }}
        />
      );
    }

    if (currentStep === 'questionnaire') {
      return (
        <UserQuestionaireForms
          formData={formData!}
          answersMap={answersMap}
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          setAnswersMap={setAnswersMap}
          commentsMap={commentsMap}
          setCommentsMap={setCommentsMap}
          onBack={() => setCurrentStep('userForm')}
          onComplete={({ skipOrg, questionAnswers }: { skipOrg: boolean; questionAnswers: QuestionAnswerDto[] }) => {
            if (skipOrg) {
              setPendingQuestionAnswers(questionAnswers);
              setCurrentStep('onboardingFinish');
            } else {
              setCurrentStep('organizationForm');
            }
          }}
        />
      );
    }

    if (currentStep === 'onboardingFinish') {
      return (
        <OnboardingFinishForm
          open
          variant={finishVariant}
          onBack={() => {
            setCurrentStep(finishVariant === 'contributor' ? 'questionnaire' : 'userForm');
          }}
          onConfirm={() => {
            if (!formData) return;
            patchUserCompleteOnboarding(pendingQuestionAnswers ?? undefined);
          }}
          loading={updatingUser}
        />
      );
    }

    if (currentStep === 'organizationForm') {
      return (
        formData && (
          <OrganizationForm
            formData={formData}
            setFormData={setFormData}
            answersMap={answersMap}
            commentsMap={commentsMap}
            onBack={handleBackFromOrg}
            onNext={handleFinishAll}
            teamName={teamName}
            setTeamName={setTeamName}
          />
        )
      );
    }
  }

  return <Router />;
};

export default OnboardingContent;
